import {
  getCompetencesforLevel,
  getNotionLabel,
  getGameTypeLabel,
  getAssociationFitAssessment,
} from '../config/shared_config';

/**
 * Atelier de génération en série — tirage aléatoire d'un exercice à produire
 * dans le périmètre défini par l'admin (types d'exercices × domaines × niveau).
 *
 * Le périmètre fixe : gameTypes[], domains[], schoolLevel, difficulty.
 * Le tirage choisit aléatoirement un type d'exercice et un triplet
 * (domaine / compétence / notion) adapté au niveau scolaire.
 */

function randomFrom(list) {
  if (!Array.isArray(list) || list.length === 0) return undefined;
  return list[Math.floor(Math.random() * list.length)];
}

/**
 * Construit la liste des triplets (domaine/compétence/notion) disponibles
 * dans les domaines sélectionnés, jusqu'au niveau scolaire demandé.
 */
export function buildNotionPool(domains = [], schoolLevel = 'CE2') {
  const byDomain = getCompetencesforLevel(schoolLevel) || {};
  const pool = [];
  (domains || []).forEach((domain) => {
    const competences = byDomain[domain] || {};
    Object.entries(competences).forEach(([competence, notions]) => {
      (notions || []).forEach((notion) => {
        pool.push({ domain, competence, notion });
      });
    });
  });
  return pool;
}

/**
 * Tire un exercice à générer dans le périmètre. Retourne null si le périmètre
 * ne couvre aucune notion (domaines vides au niveau choisi).
 *
 * `avoid` (optionnel) : signature `domain|competence|notion|gameType` du tirage
 * précédent, pour éviter de retomber immédiatement sur le même.
 */
export function drawStudioExercise(perimeter, avoid = null) {
  const { gameTypes = [], domains = [], schoolLevel = 'CE2' } = perimeter || {};
  const pool = buildNotionPool(domains, schoolLevel);
  if (gameTypes.length === 0 || pool.length === 0) return null;

  const totalCombos = pool.length * gameTypes.length;
  let draw = null;
  // Quelques tentatives pour éviter de re-tirer l'identique quand c'est possible.
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const triplet = randomFrom(pool);
    const gameType = randomFrom(gameTypes);
    const signature = `${triplet.domain}|${triplet.competence}|${triplet.notion}|${gameType}`;
    draw = { ...triplet, gameType, signature };
    if (totalCombos <= 1 || signature !== avoid) break;
  }

  const associationFit = draw.gameType === 'association'
    ? getAssociationFitAssessment(draw.domain, draw.competence, draw.notion)
    : null;

  return {
    ...draw,
    notionLabel: getNotionLabel(draw.notion) || draw.notion,
    gameTypeLabel: getGameTypeLabel(draw.gameType) || draw.gameType,
    associationWarning: associationFit && !associationFit.fit ? associationFit.reason : null,
  };
}
